import { computed, ref } from "vue";
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";

import * as tmi from "@/constants";

import { useArticlesStore } from "@/stores/Articles";
import { useAuthStore } from "@/stores/Auth";
import { useAuthorsStore } from "@/stores/Authors";
import { useBonusReportsStore } from "@/stores/BonusReports";
import { useCategoriesStore } from "@/stores/Categories";
import { useCoursesStore } from "@/stores/Courses";
import { useModelPortfoliosStore } from "@/stores/ModelPortfolios";
import { useMyPortfoliosStore } from "@/stores/MyPortfolios";
import { usePermissionsStore } from "@/stores/Permissions";
import { useProductsStore } from "@/stores/Products";
import { useSectorsStore } from "@/stores/Sectors";
import { useSecuritiesStore } from "@/stores/Security";
import { useSimplePagesStore } from "@/stores/SimplePages";
import { useTickersStore } from "@/stores/Tickers";
import { useTradeAlertsStore } from "@/stores/TradeAlerts";
import { useUserStore } from "@/stores/User";
import { useWebinarsStore } from "@/stores/Webinars";

import { ContentfulItem, HasAuthor } from "@tmi/types/entities";

import { FiveMinutesInMilliseconds } from "@/constants";

import routeManager from "@/router/route-manager";
import type { Credentials } from "@tmi/types/authentication";
import {
    articleKey,
    authorsSyncKey,
    bonusReportKey,
    categoriesSyncKey,
    productsSyncKey,
    sectorAlertsKey,
    sectorsSyncKey,
    simplePagesSyncKey,
    tickersSyncKey,
    tradeAlertKey,
    webinarKey
} from "@/types/products/product-definitions";

import { RouteLocationNormalizedLoaded, useRouter } from "vue-router";

// UTILITIES
import { useSectorHistoryStore } from "@/stores/SectorHistory";
import { createDataSyncEvent, type DataSyncEvent } from "@/utils/data-sync-event-factory";
import {
    buildStandardHeaders,
    defaultReasonForAbortingApiCall,
    goFetch,
    isSuccessState,
    normalizeFetchParameters
} from "@/utils/network-utils";
import { reportContentViewEvent } from "@/composables/user-activity-monitor";
import { useContentReadState } from "@/composables/content-read-state";

import { MessageTypes } from "@tmi/types/enums/message-types";

import type { CompositionContext } from "@/services/data/sync-supervisor";
import type { RestfulPayload, SignUpPayload, LoginPayload } from "@tmi/types/api/fetch";
import { useLocalStorage } from "@vueuse/core";
import { resetCurrentCachedData } from "@/plugins/LocalForagePlugin";

import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

let worker: Worker | null = null;
let syncInProgress: boolean = false;

const MaintenanceRefreshInterval: number = FiveMinutesInMilliseconds;

export const useApplicationStore = defineStore( 'application', () => {
    // Global references
    const router = useRouter();

    // Store references - used to clear state upon logout
    const articlesStore = useArticlesStore();
    const authStore = useAuthStore();
    const authorsStore = useAuthorsStore();
    const bonusReportsStore = useBonusReportsStore();
    const categoriesStore = useCategoriesStore();
    const coursesStore = useCoursesStore();
    const modelPortfoliosStore = useModelPortfoliosStore();
    const myPortfoliosStore = useMyPortfoliosStore();
    const permissionsStore = usePermissionsStore();
    const productsStore = useProductsStore();
    const sectorsStore = useSectorsStore();
    const sectorHistoryStore = useSectorHistoryStore();
    const securitiesStore = useSecuritiesStore();
    const simplePagesStore = useSimplePagesStore();
    const tickersStore = useTickersStore();
    const tradeAlertsStore = useTradeAlertsStore();
    const webinarsStore = useWebinarsStore();
    const userStore = useUserStore();
    const { markContentRead } = useContentReadState();

    /// STATE
    const { isAuthenticated } = storeToRefs( authStore );
    const adminSession = ref< boolean >( false );
    const authenticating = ref< boolean >( false );
    const handlingUnauthorized = ref< boolean >( false );
    // C5 fix round 1 (Finding 2): guards reHydrateTickerDependentContent against overlapping
    // passes — the tickers store and the crosswalk store can each independently flip from empty
    // to populated in the same burst (e.g. a cold load), and each fires its own UPDATE_COMPLETE.
    const reHydratingTickerDependentContent = ref< boolean >( false );

    const initializing = ref< boolean >( false );
    const publicInitialized = ref< boolean >( false );
    const privateInitialized = ref< boolean >( false );
    const shellBootstrapped = ref< boolean >( false );
    const bootstrappingShell = ref< boolean >( false );
    const loading = ref< boolean >( false );
    const isLoading = computed< boolean >( () => loading.value );
    const isShellReady = computed< boolean >( () => shellBootstrapped.value && !bootstrappingShell.value );

    const contentModalVisible = ref< boolean >( false );
    const contentItem = ref< HasAuthor | ContentfulItem | undefined >();
    const contentKey = ref< string | undefined >();

    const { isLoading: modelPortfoliosLoading, initialized: modelPortfolioInitialized } = storeToRefs( modelPortfoliosStore );

    const dateOfMostRecentMarketDataAvailable = useLocalStorage< string >( 'latest-dt', '' );

    const compositionCtx: CompositionContext = {
        getAuthorById: ( id: string ) => authorsStore.authorById( id ),
        getCategoryById: ( id: string ) => categoriesStore.categoryById( id ),
        getProductById: ( id: string ) => productsStore.productById( id ),
        getSectorById: ( id: string ) => sectorsStore.sectorById( id ),
        getSectorByTmiId: ( id: number ) => sectorsStore.sectorByTmiId( id ),
        getTickerById: ( id: string ) => tickersStore.tickerById( id ),
        // C5 (Option 1): content id -> symbol (crosswalk) -> sentient SearchableTicker (symbol).
        getTickerBySymbol: ( symbol: string ) => tickersStore.tickerBySymbol( symbol )
    };

    const supportedModelPortfoliosActions: Array< string > = [
        'switchPortfolioContext'
    ];
    modelPortfoliosStore.$onAction( async ( { name } ): Promise< void > => {
        // Rules:
        //  - must be logged in
        //  - ModelPortfolioStore must not be initialized (sliding window)
        //  - ModelPortfolioStore must not be in the process of loading
        //  - the action being intercepted must be in the list of supported actions
        if ( !isAuthenticated.value || modelPortfolioInitialized.value || modelPortfoliosLoading.value || !supportedModelPortfoliosActions.includes( name ) ) {
            return;
        }

        await modelPortfoliosStore.initializeAsync();
    } );

    /**
     * This call is not authenticated and thus does not need to first validate that the session is valid
     */
    async function refreshDateOfMostRecentMarketDataAvailable() {
        try {
            const fetchParams = normalizeFetchParameters( {
                serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
                endpoint: `${ import.meta.env.VITE_SENTIENT_REL_PATH }`,
                payload: {
                    query: `{ dateOfLatestMarketDataAvailable { latest_mk_dt } }`
                }
            } );

            const headers = buildStandardHeaders();

            // Create an AbortController to short-circuit an API call that is taking too long
            const monitor = new AbortController();
            const timeoutId = setTimeout( () => monitor.abort( defaultReasonForAbortingApiCall ), fetchParams.maxApiDuration );

            const init = {
                method: fetchParams.method,
                body: fetchParams.body,
                headers: headers,
                signal: monitor.signal
            };

            // Perform the API call using fetch or any other suitable method
            const response: Response = await fetch( fetchParams.uri, init );


            // As a generally good practice, always clean up any resources that may or may not be active
            clearTimeout( timeoutId );

            if ( response.ok ) {
                let responsePayload: any = {};
                try {
                    responsePayload = await response.json();
                } catch ( err: any ) { /* IGNORE */
                }

                if ( responsePayload?.data?.dateOfLatestMarketDataAvailable?.latest_mk_dt ) {
                    dateOfMostRecentMarketDataAvailable.value = responsePayload.data.dateOfLatestMarketDataAvailable.latest_mk_dt;
                }
            }
        } catch ( err: any ) {
            /* IGNORE */
        }
    }

    /**
     * Shared handle on the run currently in flight, so concurrent callers JOIN it.
     *
     * This used to be `if ( initializing.value ) return;`, which handed every concurrent caller an
     * already-resolved promise while the real work was still running. There are 44 sites that
     * `await appStore.initializeAsync()` before reading store data; under that guard, all but the
     * first resolved in a single microtask and carried on against empty stores. It was invisible
     * only because nothing rendered until the shell's own call had finished — the moment public
     * routes stop waiting for the shell, every one of those 44 sites becomes a live race.
     */
    let initialization: Promise< void > | null = null;

    function initializeAsync(): Promise< void > {
        if ( initialization ) {
            return initialization;
        }

        initialization = runInitialization().finally( () => {
            initialization = null;
        } );

        return initialization;
    }

    async function runInitialization(): Promise< void > {
        try {
            initializing.value = true;
            if ( !publicInitialized.value ) {
                await Promise.allSettled( [
                    refreshDateOfMostRecentMarketDataAvailable(),
                    productsStore.initializeAsync(),
                    authorsStore.initialize(),
                    categoriesStore.initialize(),
                    simplePagesStore.initializeAsync(),
                    sectorsStore.initializeAsync(),
                    tickersStore.initialize()
                ] );

                publicInitialized.value = true;
            }

            // Required to validate other authorized stores
            if ( !privateInitialized.value && isAuthenticated.value ) {
                if ( !permissionsStore.isInitialized ) {
                    permissionsStore.initializeSynchronously();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.ArticlesPermissionKey ) ) {
                    articlesStore.initialize();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.BonusReportsPermissionKey ) ) {
                    bonusReportsStore.initialize();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.SectorAlertsPermissionKey ) ) {
                    await sectorHistoryStore.initializeAsync();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.ProductTradeAlertsPermissionKey ) ) {
                    tradeAlertsStore.initialize();
                }

                if ( permissionsStore.hasModelPortfolioAccess ) {
                    await modelPortfoliosStore.initializeAsync();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.WebinarsPermissionsKey ) ) {
                    webinarsStore.initialize();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.PortfolioBuilderPermissionKey ) ) {
                    myPortfoliosStore.initialize().then();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.CoursePermissionsKey ) ) {
                    await coursesStore.initializeAsync();
                }

                privateInitialized.value = true;
            }

            fireDataSyncEvent();
        } catch ( err: any ) {
            /* IGNORE */
        } finally {
            initializing.value = false;
        }
    }

    async function bootstrapShell(): Promise< void > {
        if ( shellBootstrapped.value || bootstrappingShell.value ) {
            return;
        }

        bootstrappingShell.value = true;

        try {
            await initializeAsync();
            shellBootstrapped.value = true;
        } finally {
            bootstrappingShell.value = false;
        }
    }

    function setSelectedArticle( id: string | undefined ): void {
        if ( id ) {
            contentKey.value = articleKey;
            contentItem.value = articlesStore.articleById( id );
            markContentRead( contentItem.value as ContentfulItem | undefined, contentKey.value );
            contentModalVisible.value = true;
            reportContentViewEvent( contentItem.value, contentKey.value );
        } else {
            contentKey.value = undefined;
            contentItem.value = undefined;
            contentModalVisible.value = false;
        }
    }

    function setSelectedBonusReport( id: string | undefined ): void {
        if ( id ) {
            contentKey.value = bonusReportKey;
            contentItem.value = bonusReportsStore.bonusReportById( id );
            contentModalVisible.value = true;
            reportContentViewEvent( contentItem.value, contentKey.value );
        } else {
            contentKey.value = undefined;
            contentItem.value = undefined;
            contentModalVisible.value = false;
        }
    }

    function setSelectedTradeAlert( id: string | undefined ): void {
        if ( id ) {
            contentKey.value = tradeAlertKey;
            contentItem.value = tradeAlertsStore.tradeAlertById( id );
            markContentRead( contentItem.value as ContentfulItem | undefined, contentKey.value );
            contentModalVisible.value = true;
            reportContentViewEvent( contentItem.value, contentKey.value );
        } else {
            contentKey.value = undefined;
            contentItem.value = undefined;
            contentModalVisible.value = false;
        }
    }

    function setSelectedWebinar( id: string | undefined ): void {
        if ( id ) {
            contentKey.value = webinarKey;
            contentItem.value = webinarsStore.webinarById( id );
            markContentRead( contentItem.value as ContentfulItem | undefined, contentKey.value );
            contentModalVisible.value = true;
            reportContentViewEvent( contentItem.value, contentKey.value );
        } else {
            contentKey.value = undefined;
            contentItem.value = undefined;
            contentModalVisible.value = false;
        }
    }

    function clearSelectedContent() {
        setSelectedArticle( undefined );
        setSelectedTradeAlert( undefined );
        setSelectedWebinar( undefined );
    }

    /***********************************************************************************************
     AUTHENTICATION API
     ***********************************************************************************************/
    async function signUp( signUpInfo: SignUpPayload ): Promise< void > {
        try {
            // Set the loading state for the given store
            loading.value = true;

            const response = await goFetch< void, RestfulPayload >( {
                endpoint: `registrar/freemium/enroll`,
                payload: signUpInfo
            } );

            if ( response && isSuccessState( response.status ) ) {
                const credentials : LoginPayload = {
                    username: signUpInfo.email,
                    password: signUpInfo.password
                };

                await signIn( credentials );
            }
        } catch ( err: any ) {
            await resetApplication(true, true);

            throw err;
        } finally {
            // Processing is finished - successfully or unsuccessfully
            authenticating.value = false;
            loading.value = false;
        }
    }

    async function signIn( credentials: Credentials ): Promise< void > {
        try {
            // Set the loading state for the given store
            loading.value = true;

            // Attempt to load the initial state for the store
            await authenticate( credentials );

            // Refresh all stores
            await initializeAsync();
        } catch ( err: any ) {
            await resetApplication(true, true);

            throw err;
        } finally {
            // Processing is finished - successfully or unsuccessfully
            authenticating.value = false;
            loading.value = false;
        }

        // If there are no errors (e.g.; the store has been initialized)
        // forward the user to the initial page
        if ( isAuthenticated.value ) {
            await forwardUserToInitialPath();
        }
    }

    async function forwardUserToInitialPath(): Promise< void > {
        await routeManager.handleRedirectWithOptionalExplicitPath( router );
    }

    async function authenticate( credentials: Credentials ): Promise< void > {
        if ( isAuthenticated.value || authenticating.value ) {
            logger.info( `authenticating.value: ${ authenticating.value } || authenticated.value: ${ isAuthenticated.value }...returning` );
            return;
        }

        // Always clear the admin session variable (it is set when the authentication path is via the token assignment)
        adminSession.value = false;

        loading.value = true;
        authenticating.value = true;

        logger.debug( 'authenticating' );

        await authStore.authenticate( credentials );
        logger.debug( `authenticate: success: ${ isAuthenticated.value }` );

        if ( isAuthenticated.value ) {
            await userStore.initializeAsync();
            await permissionsStore.initializeAsync();
        }
    }

    async function authenticateWithToken( authToken: string ) : Promise< void > {
        // Validate the JWT before doing anything
        const isValidJwt = authStore.isValidJwt( authToken );
        if ( !isValidJwt ) {
            logger.error( 'Invalid JWT token provided' );
            return;
        }

        // Assign the new token FIRST before clearing anything
        // This prevents route guards from seeing an unauthenticated state
        authStore.assignToken( authToken );

        resetInternalBookkeeping();

        try {
            loading.value = true;
            logger.debug( `authenticate: success: ${ isAuthenticated.value }` );

            if ( isAuthenticated.value ) {
                await userStore.initializeAsync();
                await permissionsStore.initializeAsync();

                // Initialize all stores (products, sectors, etc.) just like signIn does
                await initializeAsync();
            }
        } catch ( err ) {
            await resetApplication( true, true );

            throw err;
        } finally {
            authenticating.value = false;
            loading.value = false;
        }

        // If there are no errors (e.g.; the store has been initialized)
        // forward the user to the initial page
        if ( isAuthenticated.value ) {
            adminSession.value = true;
            await forwardUserToInitialPath();
        }
    }

    function resetInternalBookkeeping(): void {
        // Reset all store state (but token is already set above)
        adminSession.value = false;
        authenticating.value = false;
        initializing.value = false;
        publicInitialized.value = false;
        privateInitialized.value = false;
        loading.value = false;

        articlesStore.reset();
        authorsStore.reset();
        bonusReportsStore.reset();
        categoriesStore.reset();
        coursesStore.reset();
        modelPortfoliosStore.reset();
        myPortfoliosStore.reset();
        permissionsStore.reset();
        productsStore.reset();
        sectorHistoryStore.reset();
        sectorsStore.reset();
        securitiesStore.reset();
        simplePagesStore.reset();
        tickersStore.reset();
        tradeAlertsStore.reset();
        webinarsStore.reset();
        userStore.reset();
    }

    /**
     * C5 fix round 1 (Finding 2): the tickers store and/or the crosswalk store can complete
     * their FIRST successful sweep on a LATER pass than the content stores' own first hydration
     * — e.g. the crosswalk sweep fails on pass 1 while articles hydrate from cache against an
     * empty crosswalk, then a later pass fills the crosswalk but that pass's article sweep
     * itself reports zero new/changed articles, so no `articleKey` UPDATE_COMPLETE ever fires.
     * Without this, every article/trade-alert/webinar/bonus-report hydrated during the empty
     * window keeps `tickers: []` in memory for the rest of the session, even though the data to
     * resolve them now exists in IDB.
     *
     * Re-reads (reset + initialize) the ticker-dependent content stores from IDB — the same
     * reset()/initialize() pair each already runs on its own UPDATE_COMPLETE — so this only ever
     * repeats work those stores already know how to do safely, never anything new.
     */
    async function reHydrateTickerDependentContent(): Promise< void > {
        if ( reHydratingTickerDependentContent.value ) {
            return;
        }
        reHydratingTickerDependentContent.value = true;

        try {
            articlesStore.reset();
            bonusReportsStore.reset();
            tradeAlertsStore.reset();
            webinarsStore.reset();

            await Promise.allSettled( [
                articlesStore.initialize(),
                bonusReportsStore.initialize(),
                tradeAlertsStore.initialize(),
                webinarsStore.initialize()
            ] );
        } finally {
            reHydratingTickerDependentContent.value = false;
        }
    }

    function ensureWorker(): Worker {
        if ( !worker ) {
            worker = new Worker( new URL( '@/services/data/sync-worker.ts?worker', import.meta.url ), { type: 'module' } );

            worker.onerror = function ( event: ErrorEvent ): void {
                logger.error( `Sync worker error: ${ event.message }` );
                syncInProgress = false;
                setTimeout( fireDataSyncEvent, MaintenanceRefreshInterval );
            };

            worker.onmessage = async function ( msg: MessageEvent< DataSyncEvent > ): Promise< void > {
                const messageType: MessageTypes = msg.data.eventType;
                const payload = msg.data.payload;
                if ( MessageTypes.UPDATE_COMPLETE === messageType ) {
                    if ( payload ) {
                        switch ( payload ) {
                            case articleKey:
                                articlesStore.reset();
                                articlesStore.initialize();
                                break;
                            case bonusReportKey:
                                bonusReportsStore.reset();
                                bonusReportsStore.initialize();
                                break;
                            case tradeAlertKey:
                                tradeAlertsStore.reset();
                                tradeAlertsStore.initialize();
                                break;
                            case webinarKey:
                                webinarsStore.reset();
                                webinarsStore.initialize();
                                break;
                            case authorsSyncKey:
                                authorsStore.reset();
                                await authorsStore.initialize();
                                break;
                            case categoriesSyncKey:
                                categoriesStore.reset();
                                await categoriesStore.initialize();
                                break;
                            case productsSyncKey:
                                productsStore.reset();
                                await productsStore.initializeAsync();
                                break;
                            case sectorsSyncKey:
                                sectorsStore.reset();
                                await sectorsStore.initializeAsync();
                                break;
                            case tickersSyncKey: {
                                // C5 fix round 1 (Finding 2): capture BEFORE reset — an empty
                                // catalog transitioning to populated is the signal that content
                                // hydrated earlier against an empty catalog needs a re-pass.
                                const wasEmpty = 0 === tickersStore.allTickers.length;
                                tickersStore.reset();
                                await tickersStore.initialize();
                                if ( wasEmpty && tickersStore.allTickers.length > 0 ) {
                                    void reHydrateTickerDependentContent();
                                }
                                break;
                            }
                            case simplePagesSyncKey:
                                simplePagesStore.reset();
                                await simplePagesStore.initializeAsync();
                                break;
                            case sectorAlertsKey:
                                sectorHistoryStore.reset();
                                await sectorHistoryStore.initializeAsync();
                                break;
                        }
                    }
                } else if ( MessageTypes.LOAD_COMPLETE === messageType ) {
                    syncInProgress = false;
                    setTimeout( fireDataSyncEvent, MaintenanceRefreshInterval );
                } else {
                    await handleErrorsAsync( messageType, payload );
                }
            };
        }

        return worker;
    }

    function fireDataSyncEvent() {
        if ( !isAuthenticated.value || syncInProgress ) return;
        syncInProgress = true;

        try {
            const syncEvent = createDataSyncEvent( MessageTypes.REQUEST_SECURE_SYNC, permissionsStore.authorizationGrants() );
            ensureWorker().postMessage( syncEvent );
        } catch ( err: any ) {
            logger.error( `Failed to dispatch sync event: ${ err?.message }` );
        }
    }

    if ( !!window ) {
        window.addEventListener( 'message', async function ( msg: MessageEvent< DataSyncEvent > ): Promise< void > {
            try {
                const data = msg?.data;
                if ( data && data?.typeSignifier === 'tmi_sync_evt' ) {
                    await handleErrorsAsync( msg.data.eventType, msg.data.payload );
                }
            } catch ( err: any ) {
                logger.error( `Issue posted to internal channel: ${ JSON.stringify( err?.message ) }` );
            }
        } );
    }

    async function handleErrorsAsync( messageType: MessageTypes, payload?: string | undefined ): Promise< void > {
        if ( MessageTypes.UNAUTHORIZED === messageType ) {
            await handleUnauthorizedSessionExpiry();
        } else if ( MessageTypes.FORBIDDEN === messageType ) {
            logger.error( `Received forbidden error: ${ JSON.stringify( payload ) }` );
        } else if ( MessageTypes.INTERNAL_ERROR === messageType ) {
            logger.error( `Received internal error: ${ JSON.stringify( payload ) }` );
        } else if ( MessageTypes.NOT_FOUND === messageType ) {
            logger.error( `Received not found error: ${ JSON.stringify( payload ) }` );
        } else if ( MessageTypes.CONFIGURATION === messageType ) {
            logger.error( `Received a configuration error: ${ JSON.stringify( payload ) }` );
        } else if ( MessageTypes.UNKNOWN === messageType ) {
            logger.error( `Received an unknown error: ${ JSON.stringify( payload ) }` );
        }
    }

    function isAdminSession(): boolean {
        return adminSession.value;
    }

    async function handleUnauthorizedSessionExpiry(): Promise< void > {
        if ( handlingUnauthorized.value ) {
            return;
        }

        handlingUnauthorized.value = true;

        try {
            const currentRoute: RouteLocationNormalizedLoaded = router.currentRoute.value;
            const requiresAuthentication = !currentRoute.meta?.public || !!currentRoute.meta?.permission;

            await resetApplication( true, true );

            if ( requiresAuthentication && !routeManager.isSignInPath( currentRoute.path ) ) {
                await routeManager.handleForwardToSignInAndReturnAsync( currentRoute.fullPath || currentRoute.path, router );
            }
        } finally {
            handlingUnauthorized.value = false;
        }
    }

    async function resetApplication( logout?: boolean, doNotNavigate?: boolean ) {
        if ( !!logout ) {
            terminateWorker();
            await authStore.logout();
            if ( 'caches' in window ) {
                await caches.delete( 'tmi-api-cache' );
            }
        }

        resetInternalBookkeeping();
        await initializeAsync();
        shellBootstrapped.value = true;

        if ( !!logout && !doNotNavigate ) {
            await routeManager.handleRedirectWithOptionalExplicitPath( router, routeManager.homePath() );
        }
    }

    async function logoutAndGoHome(): Promise< void > {
        await resetApplication( true );
    }

    async function nukeItFromOrbitItsTheOnlyWayToBeSure(): Promise< void > {
        await resetApplication( true );
        localStorage.clear();

        await resetCurrentCachedData();

        if ( 'caches' in window ) {
            const names = await caches.keys();
            await Promise.all( names.map( name => caches.delete( name ) ) );
        }
        if ( navigator.serviceWorker ) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all( registrations.map( r => r.unregister() ) );
        }

        // Forward the new session to the default landing page
        const currentPath: RouteLocationNormalizedLoaded | undefined = router?.currentRoute.value;
        if ( currentPath?.fullPath !== routeManager.homePath() ) {
            await router.push( routeManager.homePath() );
        } else {
            // refresh the current page
            router.go( 0 );
        }
    }

    function terminateWorker() {
        if ( worker ) {
            worker.terminate();
            worker = null;
        }
    }

    function handleRejectedPromise( event: PromiseRejectionEvent ): null {
        const rejectionReason = event.reason;
        logger.warn( `Unhandled Promise Rejection: REASON: ${ JSON.stringify( rejectionReason ) }` );

        // Make sure the browser is aware that the promise rejection was "handled"
        return null;
    }

    return {
        // STATE
        compositionCtx,
        contentModalVisible,
        contentItem,
        contentKey,
        dateOfMostRecentMarketDataAvailable,

        // GETTERS
        isLoading,
        isShellReady,

        // ACTIONS
        authenticateWithToken,
        bootstrapShell,
        clearSelectedContent,
        handleRejectedPromise,
        initializeAsync,
        isAdminSession,
        logoutAndGoHome,
        nukeItFromOrbitItsTheOnlyWayToBeSure,
        resetApplication,
        setSelectedArticle,
        setSelectedBonusReport,
        setSelectedTradeAlert,
        setSelectedWebinar,
        signIn,
        signUp,
        terminateWorker
    };
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useApplicationStore, import.meta.hot ) );
}
